// Generated by scripts/build-rules-content.mjs from country-fr.json (aligned 9 Oct 2026).
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { CountryGuide } from "../types";

export const GUIDE_FRANCE: CountryGuide = {
  id: "france",
  code: "fr",
  title: "France (Wheelabrator Group SAS)",
  shortName: "France",
  entities: ["fr"],
  areaRoles: ["fr"],
  live: true,
  cardLine:
    "Spare parts in euro, many from the Czech factory, with a carriage line proposed by order value.",
  notYet: ["Lead times are not available for French lines yet: the line shows no delivery date."],
  summary:
    "France quotes mostly spare parts in euro, many of them supplied from the Czech factory. In the old tool (Go Autonomous) France used it for only 5 to 10 % of quotes, later 30 %, and stopped mainly because freight was not handled. The price-based carriage table and the about 24 customers who pay no freight were never set up. Norican I2Q today finds the customer, matches the items, fills in prices, checks the margin, proposes the carriage line and the sales pool, and writes a reply draft. The list of customers who pay no freight and some France-only checks are planned. Sales always decides and sends.",
  howInquiriesArrive: [
    'Customers write to the French shared mailbox Contact.FR. The old orders address and an old wheelabratorgroup.com list also deliver into it, so the address the customer typed does not matter: anything that arrives in Contact.FR shows as area France. Norican I2Q reads a copy of this mailbox within a few minutes. Mail forwarded by a Norican colleague is fine: I2Q looks past the colleague to the customer who wrote first. Only requests for a price or a quotation become cases. Orders are counted but parked, and accounting, thank-you or delivery-status mail is listed under "Not an inquiry". In a long thread only the newest message is read.',
  ],
  specialities: [
    {
      id: "fr-intake-scope",
      title: "Only real inquiries, only the newest message",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Whether a mail in Contact.FR asks for a price or a quotation, and which part of the thread is new.",
      why: "In the old tool many French mails produced wrong item lines, because thank-you mails and lead-time questions were read as inquiries or whole threads were read again.",
      youSee:
        'Info: inquiries appear in "To review". Orders are parked, other mail is listed under "Not an inquiry".',
      whatToDo:
        'Handle orders in F&O as today. If a real inquiry ended up under "Not an inquiry", open it there and use "Give feedback".',
      example:
        'A customer replies "Merci, bien reçu" to a quotation you sent. I2Q does not open a new case for it.',
      registryIds: [],
      glossaryIds: ["scope-inquiries-only", "newest-message-only"],
      source:
        "goa-knowledge.md, executive summary point 3 and section 6.1 (item-group failures FR 643 mails); scope.md",
    },
    {
      id: "fr-customer",
      title: "Finding the customer",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "The sender's e-mail address against the contacts of French customer accounts in F&O, then the signature and earlier documents. France quotes the company that wrote, not an end customer behind it.",
      why: "In the old tool the customer account was right on only about half of the mails, so the customer comes first.",
      youSee:
        'Info: the customer card says the customer is certain, or shows "Suggested, please confirm". It shows "Forwarded by a colleague" when a Norican colleague passed the mail on.',
      whatToDo:
        "Check the customer on every case. Confirm the suggestion or pick another customer. Prices and margin follow the customer, so fix it first.",
      example:
        "A buyer writes from a new address at a known foundry. I2Q suggests the foundry's account because the domain matches. You confirm it.",
      registryIds: [],
      glossaryIds: ["customer-finding", "internal-forwards"],
      source:
        "goa-knowledge.md, section 2.1 (contacts with e-mail: FR 6,625); eligibility-rules-concept.md (FR quote party = writer)",
    },
    {
      id: "fr-customer-item-numbers",
      title: "Customer item numbers",
      status: "active",
      tone: "info",
      topic: "items",
      checks:
        "Whether a number in the mail is the customer's own number for one of our items, and repairs small typing differences (missing letter, dashes, spaces, upper or lower case).",
      why: "The old tool matched only about 6 % of the item numbers it read, and a French aerospace customer always writes its own item numbers.",
      youSee:
        'Info: the line shows the customer\'s text under "Enquiry" and our item under "Matched item", as "Matched" or "Suggested, please confirm".',
      whatToDo: "Confirm the suggested item or pick the right one. Your pick helps I2Q learn.",
      example:
        'The customer asks for "réf. 4471-B, qté 10". I2Q finds that 4471-B is this customer\'s number for our blast wheel blade and suggests it.',
      plannedPart:
        "About 325 French links from customer text to our item, from the old order reading tool.",
      registryIds: [],
      glossaryIds: ["customer-item-numbers"],
      source:
        "goa-knowledge.md, section 2.2 (customer item numbers, Lasernet rules FR 325) and section 3 France",
    },
    {
      id: "fr-price",
      title: "Price from F&O, and missing prices",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "The customer's own price in F&O, else the price of the customer's price list (price group), valid on the day and for the quantity asked.",
      why: "In the old tool a price that was no longer valid could still go out, and items with no price had to be requested from the Czech factory.",
      youSee:
        'Info: the price on the line. Blocked: "No price" or "Price expired" on the line, and the prepared quotation is held until the line has a valid price.',
      whatToDo:
        "For a missing price, ask the Czech factory as today and enter the price yourself in F&O. For an expired price, check with pricing before you quote.",
      example:
        "Two of five lines have no price in France. I2Q prices three lines and marks the other two. You request the two prices from CZ.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price", "price-age"],
      modes: {
        "pricing.no_price": {
          fr: "block",
        },
      },
      source:
        "goa-knowledge.md, section 2.3 (price source, no price: FR/UK request price from CZ, expired price); section 6.1",
    },
    {
      id: "fr-margin",
      title: "Margin check",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        "Where the cost is known, whether the contribution ratio of a line reaches the margin threshold for France.",
      why: "The old tool stopped low-margin quotations, and Norican wants a step a manager can release instead.",
      youSee:
        'Blocked until a manager approves: the line shows "Margin: needs approval". If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held as well. Only colleagues with margin rights see the figures. Everyone else sees the result only.',
      whatToDo:
        'Check the price. If it is right, ask your manager to release it with "Approve price". If the item, price, quantity or customer changes later, the approval must be given again.',
      example:
        'A customer asks for a large quantity at an old price. The line falls below the French threshold and shows "Margin: needs approval". Your manager approves it.',
      registryIds: ["pricing.margin"],
      glossaryIds: ["contribution-ratio"],
      modes: {
        "pricing.margin": {
          fr: "block",
        },
      },
      source:
        "goa-knowledge.md, section 2.3 (contribution ratio gate) and section 6.5 (hard blocks where a warning would do); code pricing/thresholds.py; help content.ts case page",
    },
    {
      id: "fr-item-status",
      title: "Blocked and phasing-out items",
      status: "active",
      tone: "blocked",
      topic: "items",
      checks:
        "The status of the matched item in F&O: blocked for sale, on hold, obsolete, or phasing out.",
      why: "The old tool quoted phased-out items and sometimes swapped a valid item for the old one it replaces.",
      youSee:
        'Blocked: "Blocked for sale in F&O" on the line. Warning: "Phase-out item", with the replacement number when F&O has one. Warning for a code that needs a check: "Line 2 has signal code REV, which needs a check before quoting."',
      whatToDo:
        "Quote the replacement and tell the customer the old number changed. For a blocked item, check with product support.",
      example:
        "The customer asks for a liner that is phasing out. I2Q shows the successor item. You quote the successor and name the old number in the reply.",
      registryIds: ["gate.signal_review", "gate.successor"],
      glossaryIds: ["signal-codes", "successor-items"],
      modes: {
        "gate.signal_review": {
          fr: "warn",
        },
        "gate.successor": {
          fr: "warn",
        },
      },
      source:
        "goa-knowledge.md, section 2.2 (signal codes, alternatives and successors); code pricing/lookup.py",
    },
    {
      id: "fr-reply-language",
      title: "Reply in the customer's language",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The language of the customer's mail. The reply draft uses it, and the mail is shown translated if it is not in your language.",
      why: "French sales also answer customers abroad, for example Danish customers, in French, so the language must be read from the mail, not guessed from the country.",
      youSee:
        'Info: "Translated from ..." above the mail, and a reply draft in the language of the customer.',
      whatToDo:
        "Read the draft, adjust it, copy it into Outlook and send it from Contact.FR. Planned: the French two-language reply template (French and English) as the standard frame.",
      example:
        "A customer in Spain writes in English. The draft is in English, even though the case is in France.",
      registryIds: [],
      glossaryIds: ["translation"],
      source:
        "goa-knowledge.md, section 2.5 (reply language; Issue Log FR extract #52-#53) and section 3 France (bilingual template)",
    },
    {
      id: "fr-freight-table",
      title: "Carriage by order value",
      status: "active",
      tone: "warning",
      topic: "freight",
      checks:
        "The order value of the quote, to propose the French carriage line (charge code FR_Auto) from a table in euro.",
      why: "France did not use the old tool for months, mainly because its carriage table was never set up.",
      youSee:
        'Warning in the preview: "Freight of 75 EUR is proposed for an order value of 500.01 to 2000 EUR." It is a warning in France until the list of customers who pay no freight exists. Above 50,000 EUR or with a Media line it says "Set by hand".',
      whatToDo:
        "Check whether this customer pays freight before the quotation goes out. Up to 500 EUR: 35 EUR. 500 to 2,500: 75. 2,500 to 5,000: 150. 5,000 to 10,000: 175. 10,000 to 20,000: 300. 20,000 to 50,000: 400.",
      example:
        "A quote for 1,200 EUR of spare parts gets a proposed carriage line of 75 EUR with the warning to check the customer.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          fr: "warn",
        },
      },
      source:
        "goa-knowledge.md, executive summary point 5 and section 2.5 (Freight Charges Wheelabrator Europe v3.xlsx, G/L 708510)",
    },
    {
      id: "fr-no-freight-customers",
      title: "Customers who pay no freight",
      status: "active",
      tone: "warning",
      topic: "freight",
      checks:
        "Whether the customer has an agreement to receive goods without a freight charge. About 24 French customers do, which is 20 to 30 % of French quotes.",
      why: "This one missing exception kept France from using the old tool for months.",
      youSee:
        'Today the list of French customers who pay no freight is empty. So every proposed carriage line in France is a warning: "Check whether this customer pays freight before the quotation goes out."',
      whatToDo:
        "For a customer with a no-freight agreement, remove the carriage line. If the agreement is missing or wrong in F&O, tell your manager.",
      example:
        "One of France's biggest customers asks for 30 parts worth 4,000 EUR. The preview proposes 150 EUR carriage, and you remove it because of the no-freight agreement.",
      plannedPart:
        'The list of customers who pay no freight. A customer on it gets the note "This customer pays no freight" and no carriage line.',
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          fr: "warn",
        },
      },
      source:
        "goa-knowledge.md, executive summary point 5, section 2.5 (about 24 FR customers), Part B FR and section 9.3 (workshop preferred a customer field over a list)",
    },
    {
      id: "fr-vat-delivery-location",
      title: "VAT and carriage by delivery location",
      status: "planned",
      tone: "info",
      topic: "tax",
      checks:
        "Where the goods go: within France, elsewhere in the EU, outside the EU, or to a sister company. French VAT applies only to deliveries within France, and the carriage charge must follow the same split.",
      why: "France named this a go-live blocker in the old tool, because one carriage code for all delivery places gave the wrong VAT.",
      youSee:
        "Planned: info with the delivery location group of the customer (local, EU, outside EU, sister company) and the matching carriage line.",
      whatToDo: "Until then check VAT and the carriage line in F&O when the goods leave France.",
      example:
        "A customer in Morocco gets the quote without French VAT, and the carriage line uses the export code.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md, section 2.5 (France: charge code by delivery location), section 2.1 (FR customer groups EU/EXT/IC EU/IC EXT/LOCAL), Part B rules table (FR: VAT domestic only)",
    },
    {
      id: "fr-media",
      title: "Blasting media",
      status: "active",
      tone: "warning",
      topic: "items",
      checks: "Whether a line is blasting media (item group starting with MEDIA).",
      why: "Media ships differently, so the old tool added no automatic carriage line for it.",
      youSee:
        'Warning on the line: "Line 2 is in the Media item group ..." The carriage in the preview says "Set by hand".',
      whatToDo: "Set the carriage for this line by hand, media has no automatic carriage.",
      example:
        "A customer asks for 2 tonnes of steel shot. I2Q prices it, warns on the line and proposes no carriage.",
      registryIds: ["gate.media"],
      glossaryIds: ["media-items"],
      modes: {
        "gate.media": {
          fr: "warn",
        },
      },
      source: "goa-knowledge.md, section 2.2 (Media item groups, Issue Log #18)",
    },
    {
      id: "fr-warehouse-manual",
      title: "Items to check by hand",
      status: "planned",
      tone: "warning",
      topic: "items",
      checks:
        "Whether an item comes from warehouse S1, or is one of the items France marked for manual handling (items without the ICTA setting).",
      why: "In the old tool France sent these items to manual handling, because the automatic stock and price path does not fit them.",
      youSee: 'Planned: warning on the line "check by hand".',
      whatToDo: "Check availability and price for these lines yourself before you quote.",
      example:
        "One line of an inquiry is stocked in warehouse S1. I2Q marks it, and you confirm the price and delivery time by hand.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md, section 2.2 (warehouse: FR S1 manual; Issue Log FR extract #8, #45) and section 3 France",
    },
    {
      id: "fr-minimum-order",
      title: "Minimum order value",
      status: "planned",
      tone: "info",
      topic: "price",
      checks:
        "Whether a quote is below the French minimum order value. In France the minimum differs by item group and is charged as an admin charge.",
      why: "Small quotes cost more to handle than they earn, and the old tool had no rule for it.",
      youSee: 'Planned: info hint "below the minimum order value" on the draft.',
      whatToDo:
        "Until then add the admin charge in F&O as today. The values per item group are still being agreed.",
      example:
        "A customer asks for two bolts worth 18 EUR. I2Q will hint that the quote is below the minimum.",
      registryIds: [],
      glossaryIds: ["minimum-order-value"],
      source:
        "goa-knowledge.md, section 9.1 (minimum order value, France per item group, Issue Log #55 open) and backlog S16",
    },
    {
      id: "fr-large-quote",
      title: "Large quotes",
      status: "active",
      tone: "warning",
      topic: "price",
      checks: "Whether the quote value is above 50,000 EUR.",
      why: "Above 50,000 EUR the approval rules of the group apply, so the old tool always sent such quotes to manual handling.",
      youSee:
        'Warning: "The quotation total of 62,000.00 EUR is above the approval limit of 50,000.00 EUR."',
      whatToDo: "Ask your manager to approve this quotation before it goes out.",
      example:
        "A quote for a full set of wear parts for a blast machine reaches 62,000 EUR. I2Q prepares it but warns.",
      registryIds: ["gate.doa_limit"],
      glossaryIds: ["large-quotation"],
      modes: {
        "gate.doa_limit": {
          fr: "warn",
        },
      },
      source:
        "goa-knowledge.md, section 2.3 (delegation of authority, Issue Log #11, freight matrix)",
    },
    {
      id: "fr-blocked-senders",
      title: "Portals, competitors and blocked countries",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks:
        "Whether the sender comes from a portal used for quotations (one today: a car maker's supplier portal), is a competitor, or writes from a sanctioned country.",
      why: "In the old tool these mails went to manual handling, and France even put a portal's address on the blocked list until a portal block existed.",
      youSee:
        "Blocked for all three, so that a person decides. The case is held and the queue shows the reason first.",
      whatToDo:
        "Handle these mails yourself. Export customers are not blocked for quotes in France; three countries are blocked for orders only, so you may still quote them.",
      example:
        'A price-update request comes from a car maker\'s supplier portal. The case is held with "This mail comes from a supplier portal that is not used for quotations by mail."',
      registryIds: ["gate.competitor", "gate.sanctioned", "gate.portal"],
      glossaryIds: ["competitor-domains", "sanctioned-countries", "portal-customers"],
      modes: {
        "gate.competitor": {
          fr: "block",
        },
        "gate.sanctioned": {
          fr: "block",
        },
        "gate.portal": {
          fr: "block",
        },
      },
      source:
        "goa-knowledge.md, section 2.1 (portal customers: DK/FR only a car maker's portal; competitor domains; sanctioned countries) and section 9.1 (export blocks in France, Issue Log #101)",
    },
    {
      id: "fr-portal-long-lists",
      title: "Long price-update lists",
      status: "active",
      tone: "info",
      topic: "customer",
      checks: "Inquiries from customer portals that ask to update prices for very long item lists.",
      why: "In France these requests produced huge item lists that the old tool could not handle well.",
      youSee: 'Info: many lines on one case, some "Not evaluated, check by hand".',
      whatToDo:
        "Check whether the customer wants a quotation or a price list update. A full price list update goes to pricing, not through a quotation.",
      example:
        "A portal sends 400 items for a yearly price review. You hand it to pricing instead of quoting line by line.",
      registryIds: [],
      glossaryIds: ["portal-customers"],
      source:
        "goa-knowledge.md, section 3 France (customer portal price-update requests produce huge item lists)",
    },
    {
      id: "fr-quote-header",
      title: "Quotation header for France",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The fields at the top of the F&O quotation: sales unit, sales pool, customer reference, language, validity, payment and delivery terms.",
      why: "In the old tool missing or old header data stopped quotes in F&O, and a quote went out under the wrong salesperson.",
      youSee:
        "The preview proposes the sales pool, the sales fields, the language and the customer reference. A note lists the customer fields that are not available yet, such as sales unit, payment terms and delivery terms.",
      whatToDo:
        "Fill them in F&O as today. Sales unit from the customer: inside sales IS1 or IS2, outside sales OS1 to OS3, Interco for sister companies, or X_ plus country or agent. Sales pool from the item group with the most lines. Customer reference: the customer's inquiry number. Language French unless the customer has another. Validity 30 days. Payment and delivery terms from the customer account.",
      example:
        'An inquiry with 4 Wheelabrator parts and 1 media line gets the Wheelabrator parts pool, the customer\'s sales unit OS2, reference "DA-2026-118" and 30 days validity.',
      plannedPart: "Sales unit, validity, payment terms and delivery terms on the preview.",
      registryIds: ["header.pool", "header.not_available"],
      glossaryIds: ["quotation-header", "customer-reference-fields", "quotation-validity"],
      modes: {
        "header.pool": {
          fr: "warn",
        },
        "header.not_available": {
          fr: "info",
        },
      },
      source:
        "goa-knowledge.md, section 2.4 (FR pools, sales units), section 2.5 (language fr, validity 30 days, reference), section 3 France (SALES UNITS FR.xlsx), section 9.1 (two 30-day terms, Issue Log #9, #44)",
    },
    {
      id: "fr-quote-text",
      title: "Opening and closing text",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks: "The texts printed before and after the lines of the French quotation.",
      why: "In France the opening and closing text sit together in one note in F&O, and notes about the machine must be in before the PDF is made.",
      youSee: "Planned: a suggestion for the note. Today: nothing.",
      whatToDo:
        "Add machine, serial number or service-visit notes in F&O before you print the quotation.",
      example:
        "The customer names the machine serial number. You add it to the note so the quotation shows it.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md, section 3 France (ticket 4134) and section 5 (quote text; Issue Log FR extract #48)",
    },
    {
      id: "fr-delivery-address",
      title: "Delivery address",
      status: "planned",
      tone: "warning",
      topic: "mail-and-case",
      checks:
        "Whether the mail names a delivery address; otherwise the customer's default delivery address is used.",
      why: "In the old tool the customer's own address came back as a one-time address, so it kept asking for an address in France and the UK.",
      youSee: "Planned: info with the address used, and a warning when the mail names several.",
      whatToDo:
        "Check the delivery address in F&O, especially when the customer has several sites.",
      example:
        'The mail says "livraison à notre site de Lyon". The quote uses the Lyon address, not the head office.',
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md, section 9.1 (delivery address loop, Issue Log #108, #113) and section 5 (header fields)",
    },
    {
      id: "fr-stock-lead-time",
      title: "Stock and delivery time",
      status: "active",
      tone: "warning",
      topic: "price",
      checks:
        "Stock and delivery time of the item. Many French parts ship from the Czech factory, and stock in other companies is checked by hand.",
      why: "Delivery dates were unreliable in the old tool, and users had to check stock in other companies themselves.",
      youSee:
        "Warning when the line ships after the requested date or the lead time is over 99 days. I2Q does not have the F&O lead times yet, so you rarely see these warnings today. The check for a price without a lead time is off.",
      whatToDo: "Check stock and lead time in F&O or with CZ as today before you promise a date.",
      example:
        'The customer asks "délai ?". You check the lead time from CZ and write it into the reply.',
      plannedPart: "Lead times from F&O, so these warnings show reliably.",
      registryIds: ["pricing.lead_time", "pricing.after_requested", "pricing.ship_today"],
      glossaryIds: ["lead-time-warnings"],
      modes: {
        "pricing.lead_time": {
          fr: "warn",
        },
        "pricing.after_requested": {
          fr: "warn",
        },
        "pricing.ship_today": {
          fr: "off",
        },
      },
      source:
        'goa-knowledge.md, Part B FR (goods ship from CZ, stock checked manually), Part B pain point P6, backlog S5; help content.ts "What is not there yet"',
    },
    {
      id: "fr-credit-block",
      title: "Customer on credit block",
      status: "planned",
      tone: "info",
      topic: "customer",
      checks: "Whether the customer account is on credit block in F&O.",
      why: 'A credit block stops the order, not the quotation, and the old tool\'s notice was in English only and did not say "for a quote".',
      youSee: 'Planned: info "customer on credit block". You can still quote.',
      whatToDo: "Quote as usual and tell Finance if the customer is likely to order.",
      example:
        "A blocked customer asks for a price. I2Q prepares the quote and shows the credit block note.",
      registryIds: [],
      glossaryIds: ["prospect-and-credit-block"],
      source: "goa-knowledge.md, section 2.1 (credit block) and section 9.1 (Issue Log #86, #93)",
    },
  ],
};
